import type { AxiosError } from "axios";
import type {
  AddPeoplePayload,
  PeopleDatabase,
  PeopleDatabaseRow,
  UpdatePeopleDataPayload,
} from "~/app/sales/contacts/types/common.type";

import { axios, getBackendApiErrorResponse } from "~/core/axios";
import type { BackendApiResponse } from "~/core/types/backend-api-response.type";
import type { PaginationOptions } from "~/core/types/common.type";

import type { GetOrgObjectWithFilteringBody } from "./types";

export type GetOrgPeopleRow = {
  id: string;
  people_name: string;
  people_email: string;
  [property_name: string]: string | number | null;
};

export type GetOrgPeopleResponse = {
  rowData: Array<GetOrgPeopleRow>;
};

export const getPeopleDataByOrgId = async (
  query: GetOrgObjectWithFilteringBody,
) => {
  const blankRow: PeopleDatabaseRow = {
    id: "",
    people_email: "",
    people_name: "",
  };
  const blankPagination: PaginationOptions = {
    limit: 20,
    page: 0,
    total: 0,
  };

  try {
    const { q, limit, page, filters, sortings } = query;
    const searchParams = new URLSearchParams();

    if (q) {
      searchParams.append("q", q);
    }
    if (limit) {
      searchParams.append("limit", limit.toString());
    }
    if (page) {
      searchParams.append("page", page.toString());
    }

    if (filters?.length && filters?.length > 0) {
      searchParams.append("filters", JSON.stringify(filters));
    }

    if (sortings?.length && sortings?.length > 0) {
      searchParams.append("sortings", JSON.stringify(sortings));
    }

    const { data } = await axios.get<
      BackendApiResponse<GetOrgPeopleResponse & { metadata: PaginationOptions }>
    >(`/org/people?${searchParams.toString()}`);

    if (!data.data) {
      return { rows: [blankRow], pagination: blankPagination };
    }

    const rows: PeopleDatabase["rows"] = data.data?.rowData || [];

    return { rows, pagination: data.data.metadata };
  } catch (error) {
    const err = error as AxiosError<BackendApiResponse>;
    console.error(err.response);
    return { rows: [blankRow], pagination: blankPagination };
  }
};

export const createPeople = async (data: AddPeoplePayload) => {
  return axios.post<BackendApiResponse<PeopleDatabaseRow>>("/org/people", data);
};

export const updatePeopleAttributeValue = async (
  data: UpdatePeopleDataPayload,
) => {
  return axios.patch<BackendApiResponse>("/org/people", data);
};

export const deletePeopleById = async (id: string, accessToken: string) => {
  return axios.delete<BackendApiResponse>(`/org/people/${id}`);
};

export const getClientContactDataById = async (id: string) => {
  return axios.get<BackendApiResponse>(`/org/people/${id}`);
};

type GetPeopleDataByNameAndEmailSearchResponse = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string | null;
};

export const getPeopleDataByNameAndEmailSearch = async (q: string) => {
  const searchParams = new URLSearchParams({ q });

  try {
    const { data } = await axios.get<
      BackendApiResponse<GetPeopleDataByNameAndEmailSearchResponse[]>
    >(`/org/people/search?${searchParams.toString()}`);

    return data.data || [];
  } catch (error) {
    const err = error as AxiosError<BackendApiResponse>;
    console.error(err.response);
    return [];
  }
};

export const updateContactDataByPropertyName = async (body: {
  object_id: string;
  property_name: string;
  value: any;
}) => {
  try {
    await axios.patch<BackendApiResponse>("/org/people/edit-by-property", body);
  } catch (error) {
    const err = getBackendApiErrorResponse(error);
    throw new Error(err.data.message || "Failed to update contact data");
  }
};
